import {Injectable} from "@angular/core";
import {Action, Store} from "@ngrx/store";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {SessionsListService} from "./sessions-list.service";
import * as fromRoot from "../../../../state/app.state";
import {Observable, of} from "rxjs";
import {catchError, map, mergeMap, tap} from "rxjs/operators";
import {
  DownloadVisitNotesReportFailure,
  DownloadVisitNotesReportRequest,
  DownloadVisitNotesReportSuccess,
  FetchCoachesFilterFailure,
  FetchCoachesFilterRequest,
  FetchCoachesFilterSuccess,
  FetchParticipantAttendanceFailure,
  FetchParticipantAttendanceRequest,
  FetchParticipantAttendanceSuccess,
  FetchSessionDeliveryFilterFailure,
  FetchSessionDeliveryFilterRequest,
  FetchSessionDeliveryFilterSuccess,
  FetchSessionDetailsFailure,
  FetchSessionDetailsRequest,
  FetchSessionDetailsSuccess,
  FetchSessionListFailure,
  FetchSessionListRequest,
  FetchSessionListSuccess,
  FetchSessionResourcesFailure,
  FetchSessionResourcesRequest,
  FetchSessionResourcesSuccess,
  MarkAttendanceFailure,
  MarkAttendanceRequest,
  MarkAttendanceSuccess,
  MarkParticipantDropoutFailure,
  MarkParticipantDropoutRequest,
  MarkParticipantDropoutSuccess,
  UpdateSessionDetailsFailure,
  UpdateSessionDetailsRequest,
  UpdateSessionDetailsSuccess
} from "./sessions-list.actions";
import {
  FetchSessionDeliveryFilterModel
} from "../models/fetch-session-delivery-filter.model";
import {
  FetchSessionResourcesModel
} from "../models/fetch-session-resources.model";
import {
  UpdateSessionDetailsModel
} from "../models/update-session-details.model";
import {FetchCoachesFilterModel} from "../models/fetch-coaches-filter.model";
import {FetchSessionDetailsModel} from "../models/fetch-session-details.model";
import {FetchSessionListModel} from "../models/fetch-session-list.model";
import {
  FetchParticipantAttendanceModel
} from "../models/fetch-participant-attendance.model";
import {CommonService} from "../../../shared/services/common.service";
import {environment} from "src/environments/environment";
import {ActivatedRoute} from '@angular/router';

@Injectable()
export class SessionsListEffects {

  fetchSessionDeliveryFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSessionDeliveryFilterRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(() =>
        this.sessionsListService.fetchSessionDeliveryFilter().pipe(
          map((response: FetchSessionDeliveryFilterModel["response"]) => {
            const { data, message } = response;
            return FetchSessionDeliveryFilterSuccess({
              sessionDeliveryFilter: data,
              message
            });
          }),
          catchError((error) => {
            return of(FetchSessionDeliveryFilterFailure(error.message));
          }),
          tap((action: any) => {
            if (action.type === FetchSessionDeliveryFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchSessionDeliveryFilterFailure.type) {
              // Code to execute on API Failure Action dispatch

            }
          })
        )
      )
    )
  );

  fetchSessionResources$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSessionResourcesRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sessionsListService.fetchSessionResources(payload).pipe(
          map((response: FetchSessionResourcesModel["response"]) => {
            const { data, message } = response;
            return FetchSessionResourcesSuccess({
              data,
              message
            });
          }),
          catchError(() => {
            return of(FetchSessionResourcesFailure());
          }),
          tap((action) => {
            if (action.type === FetchSessionResourcesSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchSessionResourcesFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  updateSessionDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(UpdateSessionDetailsRequest),
      map((action: any) => {
        return action;
      }),
      mergeMap(({ payload, isSessionList }) =>
        this.sessionsListService.updateSessionDetails(payload).pipe(
          map((response: UpdateSessionDetailsModel["response"]) => {
            const { data, message } = response;

            return UpdateSessionDetailsSuccess({
              sessionDetails: data,
              message
            });
          }),
          catchError(() => {
            return of(UpdateSessionDetailsFailure());
          }),
          tap((action) => {
            if (action.type === UpdateSessionDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch
              if (isSessionList) {
                this.store.dispatch(FetchSessionListRequest({ payload: { groupId: payload.groupId } }));
              }
              this.commonService.closeDialog();
              this.commonService.notification(
                action.message,
                "success"
              );
            } else if (action.type === UpdateSessionDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchSessionDetails$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSessionDetailsRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sessionsListService.fetchSessionDetails(payload).pipe(
          map((response: FetchSessionDetailsModel["response"]) => {
            const { data, message } = response;

            return FetchSessionDetailsSuccess({
              sessionDetails: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchSessionDetailsFailure());
          }),
          tap((action) => {
            if (action.type === FetchSessionDetailsSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchSessionDetailsFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchCoachesFilter$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchCoachesFilterRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sessionsListService.fetchCoachesFilter(payload).pipe(
          map((response: FetchCoachesFilterModel["response"]) => {
            const { data, message } = response;

            return FetchCoachesFilterSuccess({
              coachesFilter: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchCoachesFilterFailure());
          }),
          tap((action) => {
            if (action.type === FetchCoachesFilterSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchCoachesFilterFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchSessionList: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchSessionListRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload: FetchSessionListModel["request"]) =>
        this.sessionsListService.fetchSessionList(payload).pipe(
          map((response: FetchSessionListModel["response"]) => {
            const { data, message } = response;
            const sessionList = data;

            return FetchSessionListSuccess({
              sessionList,
              message
            });
          }),
          catchError(() => {
            return of(FetchSessionListFailure());
          }),
          tap((action) => {
            if (action.type === FetchSessionListSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchSessionListFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  fetchParticipantAttendance$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(FetchParticipantAttendanceRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sessionsListService.fetchParticipantAttendance(payload).pipe(
          map((response: FetchParticipantAttendanceModel["response"]) => {
            const { data, message } = response;
            return FetchParticipantAttendanceSuccess({
              participantAttendanceInfo: data,
              message
            });
          }),
          catchError(() => {
            return of(FetchParticipantAttendanceFailure());
          }),
          tap((action) => {
            if (action.type === FetchParticipantAttendanceSuccess.type) {
              // Code to execute on API Success Action dispatch

            } else if (action.type === FetchParticipantAttendanceFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  markAttendance$: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(MarkAttendanceRequest),
      map((action: any) => {
        return action.payload;
      }),
      mergeMap((payload) =>
        this.sessionsListService.markAttendance(payload).pipe(
          map((response) => {
            const { data, message } = response;
            return MarkAttendanceSuccess({
              payload,
              data,
              message
            });
          }),
          catchError(() => {
            return of(MarkAttendanceFailure());
          }),
          tap((action) => {
            if (action.type === MarkAttendanceSuccess.type) {
              // Code to execute on API Success Action dispatch

              this.commonService.notification('Attendance updated successfully.', 'success');

              const payload = {
                sessionId: action.payload.sessionId,
                groupId: action.payload.groupId,
              }

              this.store.dispatch(FetchSessionDetailsRequest({payload}));
            } else if (action.type === MarkAttendanceFailure.type) {
              // Code to execute on API Failure Action dispatch
            }
          })
        )
      )
    )
  );

  marParticipantDropout$ = createEffect(() => this.actions.pipe(ofType(MarkParticipantDropoutRequest), map(action => action.payload), mergeMap(payload => {
    this.commonService.startLoading();
    return this.sessionsListService.markParticipantDropout({ groupId: payload.groupId, status: payload.status, userId: payload.userId })
      .pipe(map(response => {
        return MarkParticipantDropoutSuccess({
          payload: {
            groupId: payload.groupId,
            sessionId: payload.sessionId
          }
        })
      }), catchError(err => of(MarkParticipantDropoutFailure())),
        tap((action: any) => {
          if (action.type === MarkParticipantDropoutSuccess.type) {
            // Code to execute on API Success Action dispatch
            this.commonService.closeDialog();
          } else if (action.type === MarkParticipantDropoutFailure.type) {
            // Code to execute on API Failure Action dispatch
          }
          this.commonService.stopLoading();
        }))
  })));

  markedDropout$ = createEffect(() => this.actions.pipe(ofType(MarkParticipantDropoutSuccess), map(actions => {
    return actions.payload
  }),
    mergeMap((payload) =>
      this.sessionsListService.fetchSessionDetails(payload).pipe(
        map((response: FetchSessionDetailsModel["response"]) => {
          const { data, message } = response;

          return FetchSessionDetailsSuccess({
            sessionDetails: data,
            message
          });
        }),
        catchError(() => {
          return of(FetchSessionDetailsFailure());
        }),
        tap((action) => {
          if (action.type === FetchSessionDetailsSuccess.type) {
            // Code to execute on API Success Action dispatch

          } else if (action.type === FetchSessionDetailsFailure.type) {
            // Code to execute on API Failure Action dispatch
          }
        })
      )
    )))

  generateVisitReport$ = createEffect(() =>
    this.actions.pipe(
      ofType(DownloadVisitNotesReportRequest),
      map(action => {
        this.commonService.startLoading();
        return action.payload
      }),
      mergeMap(payload =>
        this.sessionsListService.generateVisitNotesReports(payload)
          .pipe(
            map(response => {
              if (response?.data?.url) {
                const url = new URL(`${environment.cdnBaseUrl}${response?.data?.url}`)
                // appending this to avoid caching
                url.searchParams.append('t', new Date().getTime().toString());
                window.open(url.toString())
              }
              return DownloadVisitNotesReportSuccess();
            }),
        catchError(err => of(DownloadVisitNotesReportFailure())),
        tap((action) => {
          if (action.type === DownloadVisitNotesReportSuccess.type) {
            // Code to execute on API Success Action dispatch
            this.commonService.notification('Report Downloaded Successfully', 'success');
          } else if (action.type === DownloadVisitNotesReportFailure.type) {
            // Code to execute on API Failure Action dispatch
          }
          this.commonService.stopLoading();
        }))
      )))

  constructor(
    private store: Store<fromRoot.State>,
    private sessionsListService: SessionsListService,
    private commonService: CommonService,
    private actions: Actions,
    private route: ActivatedRoute
  ) {
  }
}
